<template>
  <div class="google-event-form">
    <form @submit.prevent="handleSubmit" class="event-form">
      <!-- Event Title -->
      <div class="form-group">
        <label for="title" class="form-label">Event Title</label>
        <input
          id="title"
          v-model="eventData.title"
          type="text"
          class="form-input"
          placeholder="Add title"
          required
          :maxlength="250"
        />
        <div class="char-count">{{ eventData.title.length }}/250</div>
      </div>

      <!-- Date and Time Row -->
      <div class="form-row">
        <!-- Start Date -->
        <div class="form-group flex-1">
          <label for="start-date" class="form-label">Start Date</label>
          <input
            id="start-date"
            v-model="eventData.startDate"
            type="date"
            class="form-input"
            required
          />
        </div>

        <!-- Start Time -->
        <div class="form-group flex-1">
          <label for="start-time" class="form-label">Start Time</label>
          <div class="time-input-container">
            <select
              v-model="eventData.startHour"
              class="time-select"
              :disabled="eventData.allDay"
            >
              <option value="">Hour</option>
              <option v-for="hour in hours" :key="hour" :value="hour">
                {{ hour }}
              </option>
            </select>
            <span class="time-separator">:</span>
            <select
              v-model="eventData.startMinute"
              class="time-select"
              :disabled="eventData.allDay"
            >
              <option value="">Min</option>
              <option v-for="minute in minutes" :key="minute" :value="minute">
                {{ minute }}
              </option>
            </select>
            <select
              v-model="eventData.startAmPm"
              class="time-select"
              :disabled="eventData.allDay"
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
        </div>
      </div>

      <div class="form-row">
        <!-- End Date -->
        <div class="form-group flex-1">
          <label for="end-date" class="form-label">End Date</label>
          <input
            id="end-date"
            v-model="eventData.endDate"
            type="date"
            class="form-input"
          />
        </div>

        <!-- End Time -->
        <div class="form-group flex-1">
          <label for="end-time" class="form-label">End Time</label>
          <div class="time-input-container">
            <select
              v-model="eventData.endHour"
              class="time-select"
              :disabled="eventData.allDay"
            >
              <option value="">Hour</option>
              <option v-for="hour in hours" :key="hour" :value="hour">
                {{ hour }}
              </option>
            </select>
            <span class="time-separator">:</span>
            <select
              v-model="eventData.endMinute"
              class="time-select"
              :disabled="eventData.allDay"
            >
              <option value="">Min</option>
              <option v-for="minute in minutes" :key="minute" :value="minute">
                {{ minute }}
              </option>
            </select>
            <select
              v-model="eventData.endAmPm"
              class="time-select"
              :disabled="eventData.allDay"
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
        </div>
      </div>

      <!-- All Day and Active Status Row -->
      <div class="form-row">
        <div class="form-group flex-1">
          <label class="checkbox-label">
            <input
              v-model="eventData.allDay"
              type="checkbox"
              class="checkbox-input"
              @change="handleAllDayChange"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">All day</span>
          </label>
        </div>
        <div class="form-group flex-1">
          <label class="checkbox-label">
            <input
              v-model="eventData.active"
              type="checkbox"
              class="checkbox-input"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">Event is active</span>
          </label>
        </div>
      </div>

      <!-- Event Color -->
      <div class="form-group">
        <label class="form-label">Event Color</label>
        <div class="color-picker">
          <div
            v-for="color in eventColors"
            :key="color.value"
            :class="[
              'color-option',
              { active: eventData.color === color.value },
            ]"
            :style="{ backgroundColor: color.value }"
            @click="eventData.color = color.value"
            :title="color.name"
          >
            <i
              v-if="eventData.color === color.value"
              class="fas fa-check color-check"
            ></i>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="form-group">
        <label for="description" class="form-label">Description</label>
        <ckeditor
          v-model="eventData.description"
          :editor="editor"
          :config="editorConfig"
          class="ck-editor-custom compact"
        />
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <div class="actions-left">
          <button
            v-if="isEditing"
            type="button"
            @click="handleDelete"
            class="btn btn-danger"
          >
            Delete
          </button>
        </div>
        <div class="actions-right">
          <button
            type="button"
            @click="$emit('cancel')"
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="!isFormValid"
          >
            {{ isEditing ? "Update Event" : "Create Event" }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import CKEditor from "@ckeditor/ckeditor5-vue";
import ClassicEditor from "ckeditor5-build-classic-with-font";

export default {
  name: "GoogleEventForm",
  components: {
    ckeditor: CKEditor.component,
  },
  props: {
    initialData: {
      type: Object,
      default: () => ({}),
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editor: ClassicEditor,
      eventData: {
        title: "",
        startDate: "",
        startHour: "",
        startMinute: "00",
        startAmPm: "AM",
        endDate: "",
        endHour: "",
        endMinute: "00",
        endAmPm: "AM",
        allDay: false,
        description: "",
        color: "#1a73e8",
        active: true,
      },
      eventColors: [
        { name: "Google Blue", value: "#1a73e8" },
        { name: "Red", value: "#d50000" },
        { name: "Orange", value: "#e67c73" },
        { name: "Yellow", value: "#f4b400" },
        { name: "Green", value: "#0f9d58" },
        //   { name: 'Teal', value: '#00acc1' },
        //   { name: 'Blue', value: '#4285f4' },
        //   { name: 'Purple', value: '#9c27b0' },
        //   { name: 'Pink', value: '#ad1457' },
        //   { name: 'Brown', value: '#8d6e63' },
        //   { name: 'Grey', value: '#616161' },
        //   { name: 'Black', value: '#424242' }
      ],
      hours: [
        "01",
        "02",
        "03",
        "04",
        "05",
        "06",
        "07",
        "08",
        "09",
        "10",
        "11",
        "12",
      ],
      minutes: [
        "00",
        "05",
        "10",
        "15",
        "20",
        "25",
        "30",
        "35",
        "40",
        "45",
        "50",
        "55",
      ],
    };
  },
  computed: {
    isFormValid() {
      return (
        this.eventData.title.trim().length > 0 &&
        this.eventData.startDate.length > 0
      );
    },
    editorConfig() {
      return {
        toolbar: [
          "heading",
          "|",
          "fontColor",
          "|",
          "bold",
          "link",
          "unlink",
          "bulletedList",
          "numberedList",
          "blockQuote",
          "insertTable",
          "undo",
          "redo",
        ],
        heading: {
          options: [
            {
              model: "paragraph",
              title: "Paragraph",
              class: "ck-heading_paragraph",
            },
            {
              model: "heading1",
              view: "h1",
              title: "Heading 1",
              class: "ck-heading_heading1",
            },
            {
              model: "heading2",
              view: "h2",
              title: "Heading 2",
              class: "ck-heading_heading2",
            },
            {
              model: "heading3",
              view: "h3",
              title: "Heading 3",
              class: "ck-heading_heading3",
            },
          ],
        },
        table: {
          contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
        },
        fontColor: {
          colors: [
            {
              color: "#005EB8",
              label: "Blue",
            },
            {
              color: "#FF2E2E",
              label: "Red",
            },
            {
              color: "#4F535B",
              label: "Main Grey",
            },
          ],
        },
        link: {
          defaultProtocol: 'https://',
          decorators: {
            toggleDownloadable: {
              mode: "manual",
              label: "Downloadable",
              attributes: {
                download: "file",
              },
            },
            openInNewTab: {
              mode: "manual",
              label: "Open in a new tab",
              attributes: {
                target: "_blank",
                rel: "noopener noreferrer",
              },
            },
          },
        },
      };
    },
  },
  watch: {
    initialData: {
      immediate: true,
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.populateForm(newVal);
        } else {
          this.resetForm();
        }
      },
    },
    // Auto-populate end time when start time is complete
    "eventData.startHour": function () {
      this.autoPopulateEndTime();
    },
    "eventData.startMinute": function () {
      this.autoPopulateEndTime();
    },
    "eventData.startAmPm": function () {
      this.autoPopulateEndTime();
    },
  },
  methods: {
    populateForm(data) {
      const startTime =
        data.start_time || data.additionalProp1?.start_time || "";
      const endTime = data.end_time || data.additionalProp1?.end_time || "";

      // Parse start time
      const startTimeParts = this.parseTime24To12(startTime);
      const endTimeParts = this.parseTime24To12(endTime);

      this.eventData = {
        title: data.title || "",
        startDate: data.date || data.additionalProp1?.date || "",
        startHour: startTimeParts.hour,
        startMinute: startTimeParts.minute,
        startAmPm: startTimeParts.ampm,
        endDate:
          data.end_date ||
          data.additionalProp1?.end_date ||
          data.date ||
          data.additionalProp1?.date ||
          "",
        endHour: endTimeParts.hour,
        endMinute: endTimeParts.minute,
        endAmPm: endTimeParts.ampm,
        allDay: !startTime && !endTime,
        description:
          data.description || data.additionalProp1?.description || "",
        color: data.color || data.additionalProp1?.color || "#1a73e8",
        active: data.active !== undefined ? data.active : true,
      };
    },

    resetForm() {
      const today = new Date().toISOString().split("T")[0];
      this.eventData = {
        title: "",
        startDate: today,
        startHour: "",
        startMinute: "00",
        startAmPm: "AM",
        endDate: today,
        endHour: "",
        endMinute: "00",
        endAmPm: "AM",
        allDay: false,
        description: "",
        color: "#1a73e8",
        active: true,
      };
    },

    handleAllDayChange() {
      if (this.eventData.allDay) {
        this.eventData.startHour = "";
        this.eventData.startMinute = "00";
        this.eventData.startAmPm = "AM";
        this.eventData.endHour = "";
        this.eventData.endMinute = "00";
        this.eventData.endAmPm = "AM";
      }
    },

    handleDelete() {
      this.$emit("delete");
    },

    autoPopulateEndTime() {
      // Only auto-populate if start time is complete and end time is not set
      if (
        this.eventData.startHour &&
        this.eventData.startMinute &&
        this.eventData.startAmPm &&
        !this.eventData.endHour &&
        !this.eventData.allDay
      ) {
        // Add 1 hour to start time for default end time
        let endHour = parseInt(this.eventData.startHour);
        let endAmPm = this.eventData.startAmPm;

        endHour += 1;

        // Handle hour overflow
        if (endHour > 12) {
          endHour = 1;
          endAmPm = endAmPm === "AM" ? "PM" : "AM";
        } else if (endHour === 13) {
          endHour = 1;
          endAmPm = "PM";
        }

        this.eventData.endHour = endHour.toString().padStart(2, "0");
        this.eventData.endMinute = this.eventData.startMinute;
        this.eventData.endAmPm = endAmPm;
      }
    },

    getTextLength(htmlContent) {
      // Strip HTML tags to count actual text length
      const textContent = htmlContent.replace(/<[^>]*>/g, "");
      return textContent.length;
    },

    parseTime24To12(time24) {
      if (!time24) return { hour: "", minute: "00", ampm: "AM" };

      const [hours, minutes] = time24.split(":");
      const hour24 = parseInt(hours);

      let hour12 = hour24;
      let ampm = "AM";

      if (hour24 === 0) {
        hour12 = 12;
        ampm = "AM";
      } else if (hour24 === 12) {
        hour12 = 12;
        ampm = "PM";
      } else if (hour24 > 12) {
        hour12 = hour24 - 12;
        ampm = "PM";
      } else if (hour24 > 0 && hour24 < 12) {
        ampm = "AM";
      }

      return {
        hour: hour12.toString().padStart(2, "0"),
        minute: minutes || "00",
        ampm: ampm,
      };
    },

    convertTime12To24(hour, minute, ampm) {
      // Return empty if essential parts are missing
      if (!hour || !ampm) return "";

      // Default minute to '00' if not provided
      const finalMinute = minute || "00";

      let hour24 = parseInt(hour);

      if (ampm === "AM" && hour24 === 12) {
        hour24 = 0;
      } else if (ampm === "PM" && hour24 !== 12) {
        hour24 = hour24 + 12;
      }

      return `${hour24.toString().padStart(2, "0")}:${finalMinute}`;
    },

    handleSubmit() {
      if (!this.isFormValid) return;

      // Convert 12-hour format to 24-hour format
      const startTime24 = this.eventData.allDay
        ? ""
        : this.convertTime12To24(
            this.eventData.startHour,
            this.eventData.startMinute,
            this.eventData.startAmPm
          );

      const endTime24 = this.eventData.allDay
        ? ""
        : this.convertTime12To24(
            this.eventData.endHour,
            this.eventData.endMinute,
            this.eventData.endAmPm
          );

      const formattedData = {
        title: this.eventData.title.trim(),
        description: this.eventData.description.trim(),
        date: this.eventData.startDate,
        start_time: startTime24,
        end_time: endTime24,
        active: this.eventData.active,
        additionalProp1: {
          date: this.eventData.startDate,
          end_date: this.eventData.endDate,
          start_time: startTime24,
          end_time: endTime24,
          description: this.eventData.description.trim(),
          color: this.eventData.color,
          all_day: this.eventData.allDay,
        },
      };

      this.$emit("submit", formattedData);
    },
  },
};
</script>

<style scoped>
.google-event-form {
  background: #ffffff;
  border-radius: 8px;
  padding: 0;
  font-family: "Google Sans", "Roboto", -apple-system, BlinkMacSystemFont,
    "Segoe UI", sans-serif;
}

.event-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-row {
  display: flex;
  gap: 16px;
}

.flex-1 {
  flex: 1;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #3c4043;
  margin: 0;
}

.form-input {
  background: #ffffff;
  border: 1px solid #dadce0;
  border-radius: 4px;
  padding: 12px 16px;
  font-size: 14px;
  color: #3c4043;
  transition: all 0.15s ease;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
}

.form-input:hover:not(:focus) {
  border-color: #5f6368;
}

/* Time Input Styling */
.time-input-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.time-separator {
  font-size: 16px;
  font-weight: 500;
  color: #5f6368;
  margin: 0 4px;
}

.time-select {
  background: #ffffff;
  border: 1px solid #dadce0;
  border-radius: 4px;
  padding: 12px 12px;
  font-size: 14px;
  color: #3c4043;
  transition: all 0.15s ease;
  font-family: inherit;
  cursor: pointer;
  min-width: 80px;
  flex: 1;
}

.time-select:focus {
  outline: none;
  border-color: #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
}

.time-select:hover:not(:focus):not(:disabled) {
  border-color: #5f6368;
}

.time-select:disabled {
  background: #f8f9fa;
  color: #9aa0a6;
  cursor: not-allowed;
  border-color: #e8eaed;
}

.form-textarea {
  background: #ffffff;
  border: 1px solid #dadce0;
  border-radius: 4px;
  padding: 12px 16px;
  font-size: 14px;
  color: #3c4043;
  transition: all 0.15s ease;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
}

.form-textarea:focus {
  outline: none;
  border-color: #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
}

.form-textarea:hover:not(:focus) {
  border-color: #5f6368;
}

.char-count {
  font-size: 12px;
  color: #5f6368;
  text-align: right;
  margin-top: -2px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin: 0;
}

.checkbox-input {
  display: none;
}

.checkbox-custom {
  width: 18px;
  height: 18px;
  border: 2px solid #5f6368;
  border-radius: 2px;
  position: relative;
  transition: all 0.15s ease;
}

.checkbox-input:checked + .checkbox-custom {
  background: #1a73e8;
  border-color: #1a73e8;
}

.checkbox-input:checked + .checkbox-custom::after {
  content: "✓";
  position: absolute;
  top: -2px;
  left: 2px;
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.checkbox-text {
  font-size: 14px;
  color: #3c4043;
  user-select: none;
}

.color-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.color-option {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  position: relative;
}

.color-option:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.color-option.active {
  border-color: #5f6368;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.3);
}

.color-check {
  color: white;
  font-size: 12px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid #e8eaed;
}

.actions-left {
  display: flex;
}

.actions-right {
  display: flex;
  gap: 12px;
}

.btn {
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
  font-family: inherit;
}

.btn-secondary {
  background: transparent;
  color: #1a73e8;
  border: 1px solid #dadce0;
}

.btn-secondary:hover {
  background: #f8f9fa;
}

.btn-primary {
  background: #1a73e8;
  color: white;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3),
    0 1px 3px 1px rgba(60, 64, 67, 0.15);
}

.btn-primary:hover:not(:disabled) {
  background: #1557b0;
  box-shadow: 0 1px 3px rgba(60, 64, 67, 0.3),
    0 4px 8px 3px rgba(60, 64, 67, 0.15);
}

.btn-primary:disabled {
  background: #f1f3f4;
  color: #9aa0a6;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-danger {
  background: #d73027;
  color: white;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3),
    0 1px 3px 1px rgba(60, 64, 67, 0.15);
}

.btn-danger:hover:not(:disabled) {
  background: #b71c1c;
  box-shadow: 0 1px 3px rgba(60, 64, 67, 0.3),
    0 4px 8px 3px rgba(60, 64, 67, 0.15);
}

/* CKEditor Custom Styling */
.ck-editor-custom .ck.ck-editor {
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-family: inherit;
  position: relative;
  z-index: 1;
}

.ck-editor-custom .ck.ck-toolbar {
  z-index: 2 !important;
  position: relative;
}

.ck-editor-custom .ck-editor__editable {
  min-height: 80px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
}

.ck-editor-custom.compact .ck-editor__editable {
  min-height: 60px;
}

.ck-editor-custom .ck-editor__editable:focus {
  outline: none;
  border-color: #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
}

/* Style links inside CKEditor content */
.ck-editor-custom .ck-editor__editable a {
  color: #1a73e8 !important;
  text-decoration: underline !important;
  cursor: pointer;
}

.ck-editor-custom .ck-editor__editable a:hover {
  color: #1557b0 !important;
  text-decoration: underline !important;
}

/* Override inline styles on spans inside links */
.ck-editor-custom .ck-editor__editable a span,
.ck-editor-custom .ck-editor__editable a * {
  color: inherit !important;
}

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 20px;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style>
